import { useEffect, useRef, useState } from 'react';
import { safeJsonParse } from './safe-json';
import type { LedgerGroup } from './ledger-labels';
import { LATEST_SESSION_PAGE, latestSessionEngine, latestSessionRegion } from './calculator-engine';

/** What the latest session says about where it ran: each `null` when there is no session or it names none. */
export interface LatestUsage {
  engine: string | null;
  region: string | null;
}

/**
 * The engine, and the Qwen region, the visitor's latest usage session ran on,
 * for the calculator to open on.
 *
 * `undefined` while the request is in flight; afterwards an object whose
 * fields are `null` when there is no session, the session names no region
 * (Soniox's) or the request failed — the calculator treats all of those as "no
 * hint", but only `undefined` means one may still arrive. Its own small
 * request, like `RecentRecords`, so a failure here costs nothing but the hint.
 * One page, not one row: the ledger's usage kind lists voice previews too, and
 * the newest of those is not a session (`latestSessionEngine`).
 */
export function useLatestUsageEngine(): LatestUsage | undefined {
  const [latest, setLatest] = useState<LatestUsage | undefined>(undefined);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const params = new URLSearchParams({ kind: 'usage', limit: String(LATEST_SESSION_PAGE) });
        const response = await fetch(`/api/wallet/ledger?${params}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`ledger ${response.status}`);
        const data = await safeJsonParse<{ groups: LedgerGroup[] }>(response);
        const groups = data?.groups ?? [];
        setLatest({ engine: latestSessionEngine(groups), region: latestSessionRegion(groups) });
      } catch (err) {
        console.error('Error fetching the latest session engine:', err);
        setLatest({ engine: null, region: null });
      }
    })();
  }, []);

  return latest;
}
